/* ss-bundle-builder — quick view
 *
 * Every product card links to the PDP. Following that link is a dead end for the bundle:
 * adding to cart from a product page creates a plain line with no _bundle_id, so it gets no
 * discount and does not count toward a tier. The shopper thinks they have added to their
 * bundle; they have not.
 *
 * This intercepts those links and opens a drawer instead — gallery, price, description and
 * an Add to bundle button — so nobody leaves the builder to look at a product. The full PDP
 * is still reachable, but in a new tab so the bundle in progress survives.
 *
 * Adding from the drawer clicks the card's own toggle rather than duplicating selection
 * logic, so there is still one source of truth for what is in the bundle.
 *
 * Note on buttons: the theme's global reset sets text-align:start, so every button here has
 * to centre itself explicitly or the label sits hard left.
 */
(function () {
  'use strict';

  var CSS = [
    '.bbq-back{position:fixed;inset:0;z-index:80;background:rgba(58,44,32,.45);opacity:0;transition:opacity .25s ease}',
    '.bbq-back.is-on{opacity:1}',
    '.bbq{position:fixed;z-index:81;top:0;right:0;bottom:0;width:min(460px,100%);background:#fff;display:flex;flex-direction:column;transform:translateX(100%);transition:transform .3s cubic-bezier(.22,1,.36,1);box-shadow:-8px 0 30px rgba(58,44,32,.18)}',
    '.bbq.is-on{transform:none}',
    '.bbq-head{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:16px 20px;border-bottom:1px solid var(--bb-rule,#E3D8CC);flex:none}',
    '.bbq-eyebrow{font-size:10.5px;letter-spacing:.16em;text-transform:uppercase;font-weight:700;color:var(--bb-muted,#6B5A48)}',
    '.bbq-x{border:0;background:none;display:grid;place-items:center;text-align:center;font-size:24px;line-height:1;cursor:pointer;color:var(--bb-ink,#3A2C20);padding:0 4px}',
    '.bbq-body{flex:1;overflow-y:auto;padding:20px}',
    '.bbq-hero{width:100%;aspect-ratio:1/1;object-fit:cover;border-radius:12px;display:block;background:var(--bb-soft,#F7F1E6)}',
    '.bbq-thumbs{display:flex;gap:8px;margin-top:10px;overflow-x:auto;scrollbar-width:none}',
    '.bbq-thumbs::-webkit-scrollbar{display:none}',
    '.bbq-thumbs img{width:56px;height:56px;object-fit:cover;border-radius:8px;cursor:pointer;flex:none;border:2px solid transparent}',
    '.bbq-thumbs img.is-on{border-color:var(--bb-ink,#3A2C20)}',
    '.bbq-title{font-family:var(--heading-font-family,inherit);font-weight:700;font-size:20px;line-height:1.2;color:var(--bb-ink,#3A2C20);margin:18px 0 6px}',
    '.bbq-price{display:flex;align-items:baseline;gap:10px;margin:0 0 14px;font-size:17px;font-weight:700;color:var(--bb-ink,#3A2C20)}',
    '.bbq-was{font-size:14px;font-weight:500;color:var(--bb-muted,#6B5A48);text-decoration:line-through}',
    '.bbq-desc{font-size:13.5px;line-height:1.6;color:var(--bb-text,#4A3A2C)}',
    '.bbq-desc p{margin:0 0 10px}.bbq-desc ul{margin:0 0 10px;padding-left:18px}.bbq-desc img{max-width:100%;height:auto;border-radius:8px}',
    '.bbq-pdp{display:inline-block;margin-top:14px;font-size:13px;font-weight:600;color:var(--bb-ink,#3A2C20);border-bottom:1.5px solid var(--bb-ink,#3A2C20);text-decoration:none;padding-bottom:1px}',
    '.bbq-foot{flex:none;padding:16px 20px;border-top:1px solid var(--bb-rule,#E3D8CC);background:#fff}',
    '.bbq-add{width:100%;min-height:52px;display:flex;align-items:center;justify-content:center;text-align:center;border-radius:99px;background:var(--bb-ink,#3A2C20);color:#fff;border:1.5px solid var(--bb-ink,#3A2C20);font-family:var(--heading-font-family,inherit);font-weight:700;font-size:14.5px;cursor:pointer;box-shadow:0 3px 10px rgba(58,44,32,.22)}',
    '.bbq-add:hover{background:#fff;color:var(--bb-ink,#3A2C20)}',
    '.bbq-add.is-in{background:#fff;color:var(--bb-ink,#3A2C20)}',
    '@media(max-width:600px){.bbq{top:auto;left:0;width:100%;height:88vh;border-radius:18px 18px 0 0;transform:translateY(100%)}.bbq.is-on{transform:none}}',
    '@media(prefers-reduced-motion:reduce){.bbq,.bbq-back{transition:none}}'
  ].join('');

  function css() {
    if (document.getElementById('bbq-css')) return;
    var s = document.createElement('style');
    s.id = 'bbq-css';
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  function money(c) { return '$' + (c / 100).toFixed(2); }

  var back, panel, lastFocus;

  function close() {
    if (!panel) return;
    panel.classList.remove('is-on');
    back.classList.remove('is-on');
    document.body.style.overflow = '';
    setTimeout(function () {
      if (panel) panel.remove();
      if (back) back.remove();
      panel = back = null;
    }, 260);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function open(bb, card) {
    css();
    lastFocus = document.activeElement;

    back = document.createElement('div');
    back.className = 'bbq-back';
    panel = document.createElement('div');
    panel.className = 'bbq';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.innerHTML =
      '<div class="bbq-head"><span class="bbq-eyebrow">Quick view</span>' +
      '<button type="button" class="bbq-x" aria-label="Close">&times;</button></div>' +
      '<div class="bbq-body"><p style="color:#6B5A48;font-size:13px">Loading…</p></div>';

    document.body.appendChild(back);
    document.body.appendChild(panel);
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(function () { back.classList.add('is-on'); panel.classList.add('is-on'); });

    back.addEventListener('click', close);
    panel.querySelector('.bbq-x').addEventListener('click', close);

    fetch('/products/' + card.dataset.handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (p) { fill(bb, card, p); })
      .catch(function () {
        // If the fetch fails, do not strand them — send them to the real page.
        window.location.href = '/products/' + card.dataset.handle;
      });
  }

  function fill(bb, card, p) {
    if (!panel) return;
    var imgs = (p.images || []).slice(0, 6);
    var hero = imgs[0] || card.dataset.img;
    var price = p.price;
    var compare = p.compare_at_price;

    var thumbs = imgs.length > 1
      ? '<div class="bbq-thumbs">' + imgs.map(function (src, i) {
          return '<img src="' + src + '" alt="" class="' + (i === 0 ? 'is-on' : '') + '" loading="lazy">';
        }).join('') + '</div>'
      : '';

    panel.querySelector('.bbq-body').innerHTML =
      '<img class="bbq-hero" src="' + hero + '" alt="" width="460" height="460">' +
      thumbs +
      '<h2 class="bbq-title">' + p.title + '</h2>' +
      '<p class="bbq-price">' + money(price) +
        (compare && compare > price ? '<span class="bbq-was">' + money(compare) + '</span>' : '') +
      '</p>' +
      '<div class="bbq-desc">' + (p.description || '') + '</div>' +
      '<a class="bbq-pdp" href="/products/' + p.handle + '" target="_blank" rel="noopener">View full product page &#8599;</a>';

    var heroEl = panel.querySelector('.bbq-hero');
    panel.querySelectorAll('.bbq-thumbs img').forEach(function (t) {
      t.addEventListener('click', function () {
        heroEl.src = t.src;
        panel.querySelectorAll('.bbq-thumbs img').forEach(function (o) { o.classList.remove('is-on'); });
        t.classList.add('is-on');
      });
    });

    var foot = document.createElement('div');
    foot.className = 'bbq-foot';
    foot.innerHTML = '<button type="button" class="bbq-add"></button>';
    panel.appendChild(foot);

    var btn = foot.querySelector('.bbq-add');
    function sync() {
      var inBundle = bb.sel.has(card.dataset.vid);
      btn.textContent = inBundle ? 'Added to your bundle · Remove' : 'Add to bundle · ' + money(price);
      btn.classList.toggle('is-in', inBundle);
    }
    sync();

    btn.addEventListener('click', function () {
      // Reuse the card's own toggle so selection state lives in one place.
      var toggle = card.querySelector('[data-bb-toggle]');
      if (toggle) toggle.click();
      sync();
      if (bb.sel.has(card.dataset.vid)) setTimeout(close, 220);
    });
  }

  function cardFor(el) {
    return el.closest ? el.closest('[data-bb-card]') : null;
  }

  function boot() {
    document.querySelectorAll('bundle-builder').forEach(function (bb) {
      if (bb._qvReady) return;
      bb._qvReady = true;

      bb.addEventListener('click', function (e) {
        var card = cardFor(e.target);
        if (!card) return;

        // the quick-view eye, or any link out to the product page
        var eye = e.target.closest('.quick-view-button, .product-card__quick-view');
        var link = e.target.closest('a[href*="/products/"]');
        if (!eye && !link) return;

        e.preventDefault();
        e.stopPropagation();
        open(bb, card);
      }, true);
    });
  }

  document.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
